import {EventEmitter, Injectable} from '@angular/core';
import {ApiClientService} from './api-client.service';
import {ParkingPass} from '../types/models';
import {UrlService} from './url.service';
import {EmptyRowService} from './empty-row.service';
import {ConstantsService} from './constants.service';
import {AccountPermissionsService} from './account-permissions.service';
import {AuthenticationService} from './authentication.service';
import {filter, tap} from 'rxjs/operators';
import {createInitialNetworkEntity, resolveNetworkEntity} from './helper-functions';

declare const toastr: any;
declare const swal: any;
declare const $: any;
declare const moment: any;

@Injectable()
export class ParkingPassService {
    view = 'list';
    isParkingPassModalShow = false;
    isViewModalShow = false;
    isPaymentModalShow = false;
    showDetailedView = false;
    isPrefilledFromDirectory = false;
    limits = [];
    residentParkinglots = [];

    parkingPassInParkingPassModal: ParkingPass;

    data = {
        allParkingPasses: {
            get: '/v1/booking.json?expand=building,suite,resident,createdBy,visitor,payments,bookingPassTypes,bookingPassTypes.passType,parkingLot,parkingLot.parkingRule,parkingLot.parkingRule.passTypes,bookingParkingLot,parkingLot.parkingRule.limitGroups,parkingRule,parkingRule.passTypes',
            data: [],
            total: null
        },
    };
    tabId = 'allParkingPasses';
    spinnerShow = true;
    showServiceUnavailable = false;
    buildings: Array<any> = [];
    buildingsForRead: Array<any> = [];
    buildingsForUpdate: Array<any> = [];
    serviceName: string = 'parking_pass';
    selectedItemData = null;
    historyLoading = false;
    history = [];
    onDetailedViewToggle: EventEmitter<any> = new EventEmitter();

    constructor(
        public api: ApiClientService,
        public urlService: UrlService,
        public emptyRowService: EmptyRowService,
        public account: AccountPermissionsService,
        public authService: AuthenticationService
    ) {
        this.api.onLogout.subscribe(() => this.buildings = []);
    }

    getData(scrollToTop?) {
        this.getDataByQueryParams(scrollToTop).subscribe();
        this.getLimits();
    }

    getDataByQueryParams(scrollToTop?: boolean) {
        this.showServiceUnavailable = false;
        this.spinnerShow = true;
        this.data[this.tabId].total = null;

        const tabId = this.urlService.queryParams['tab'];
        let url = this.data[tabId].get;
        const queryParams = this.urlService.getQueryParamsString();

        if (this.authService.userIsResident && localStorage.getItem('residentselectedBuilding'))
            url += `&building_id=${JSON.parse(localStorage.getItem('residentselectedBuilding')).buildingId}`;
        return this.api.getJson('domainParkingPass', `${url}${queryParams}`)
            .pipe(
                createInitialNetworkEntity(),
                resolveNetworkEntity((entity: any) => {
                    if (entity.building && entity.building.buildingId) { // @warning buildingId is only in this service, everywhere else is bdId
                        entity.building.id = entity.building.buildingId;
                    }
                    return ParkingPass.fromExistingData(this.account.addPermissionsToEntity(entity, this.serviceName));
                }),
                tap(({finished}) => this.spinnerShow = !finished),
                tap(
                    (body) => {
                        this.data[tabId].data = body.entities;
                        this.data[tabId].total = body.total;
                        this.emptyRowService.fillEmptyRow();
                        if ($('.table')[0] && scrollToTop)
                            $('.table')[0].scrollIntoView();
                    },
                    (error) => {
                        this.showServiceUnavailable = true;
                        this.spinnerShow = false;
                        this.api.errorHandler(error, 'Failed get Parking Passes');
                    }
                ),
                filter(({finished}) => finished),
                tap(() => {
                    if (this.parkingPassInParkingPassModal && this.view != 'calendar') {
                        this.data[tabId].data.forEach(item => {
                            if (this.parkingPassInParkingPassModal.id == item.id)
                                this.parkingPassInParkingPassModal = JSON.parse(JSON.stringify(item));
                        });
                    }
                    if (this.selectedItemData) {
                        this.selectedItemData = this.data.allParkingPasses.data.find(si => si.id == this.selectedItemData.id);
                        if (this.selectedItemData)
                            this.selectedItemData.checked = true;
                        else
                            this.showDetailedView = false;
                    }
                })
            );
    }

    getBuildings(): void {
        const url = `/v1/parking_pass_buildings.json?per-page=1000&order-by=buildingName|asc&expand=parkingLots`;
        this.api.get('domainParkingPass', url).subscribe(
            (res) => {
                this.buildings = JSON.parse(res._body).entities.map(building => ({
                    id: building.buildingId,
                    name: building.buildingName,
                    timezone: building.timeZone,
                    parkingLots: building.parkingLots
                }));
                this.getFilteredBuildings();
            },
            (error) => this.api.errorHandler(error, 'Failed get buildings')
        );
    }

    resetData() {
        for (const item in this.data) {
            this.data[item].data = [];
            this.data[item].total = null;
        }
        this.showDetailedView = false;
        this.selectedItemData = null;
    }

    editParkingPass(parkingPass: ParkingPass) {
        this.isParkingPassModalShow = true;
        this.parkingPassInParkingPassModal = JSON.parse(JSON.stringify(parkingPass));
    }

    showPaymentModal(parkingPass: ParkingPass) {
        this.isPaymentModalShow = true;
        this.parkingPassInParkingPassModal = JSON.parse(JSON.stringify(parkingPass));
    }

    deleteParkingPass(id: number): void {
        if (!id) return;
        swal(ConstantsService.getSwalConfig('Delete Parking Pass'),
            () => {
                const url = `/v1/booking/${id}.json`;
                this.api.remove('domainParkingPass', url).subscribe(
                    () => {
                        toastr.success('Parking Pass has been deleted.');
                        if (this.data.allParkingPasses.data.length - 1 == 0 && this.urlService.queryParams.page != '1')
                            this.urlService.queryParams.page = (parseInt(this.urlService.queryParams.page) - 1).toString();
                        this.getData();
                    },
                    (error) => this.api.errorHandler(error, 'Delete Parking Pass failed')
                );
            }
        );
    }

    canEdit(buildingId?: number) {
        return this.account.canUpdate(this.serviceName, buildingId);
    }

    canDelete(buildingId?: number) {
        return this.account.canDelete(this.serviceName, buildingId);
    }

    getFilteredBuildings() {
        this.buildingsForRead = this.account.getBuildingsForRead(this.buildings, this.serviceName);
        this.buildingsForUpdate = this.account.getBuildingsForUpdate(this.buildings, this.serviceName);
    }

    getLimits() {
        this.limits = [];
        if (!JSON.parse(localStorage.getItem('residentselectedBuilding'))) return;
        const selectedBuilding = JSON.parse(localStorage.getItem('residentselectedBuilding'));
        const sortedByBuilding = `&building_id=${selectedBuilding.buildingId}&suite_id=${selectedBuilding.suite_id}`;
        const url = `/v1/available_pass_types.json?suite_id=4&building_id=4${sortedByBuilding}`;
        this.api.get('domainParkingPass', url).subscribe(
            (res) => {
                const body = JSON.parse(res._body);
                body.infoGroupedByParkingRule.forEach(element => {
                    element.passTypes.forEach(element => {
                        if (element.limits.perSuiteLimit) {
                            this.limits.push({
                                passTypeName: element.passTypeName,
                                limits: element.limits.perSuiteLimit.left
                            });
                        }
                    });
                });
            },
            (error) => this.api.errorHandler(error, 'Failed get limits')
        );
    }

    getLots(buildingId) {
        const url = `/v1/parking_lot.json?expand=createdBy,parkingRule,parkingRule.passTypes,lotRule,lotRule.parkingRule,lotRule.parkingRule.passTypes&building_id=${buildingId}&enable_status=1&parking_rule=is_not_null_value&parking_rule_status=1`;
        return this.api.get('domainParkingPass', url);
    }

    toggleDetailedView(data, state: boolean): void {
        this.data.allParkingPasses.data.forEach(item => item.checked = false);
        this.selectedItemData = data ? data : null;
        this.showDetailedView = state;
        if (this.selectedItemData) {
            this.selectedItemData.checked = true;
            setTimeout(() => this.onDetailedViewToggle.emit(), 500);
            // this.getHistory();
            // for (const field in this.selectedItemData.formFields)
            //     this.selectedItemData.formFields[field].editMode = false;
        }
    }

    getHistory() {
        if (this.selectedItemData) {
            this.historyLoading = true;
            const url = `/v1/history.json?per-page=10000&objectId=${this.selectedItemData.id}`;
            this.api.get('domainAmenities', url).subscribe(
                (res) => {
                    this.historyLoading = false;
                    this.history = JSON.parse(res._body).entities;
                },
                (error) => {
                    this.historyLoading = false;
                    this.api.errorHandler(error, 'Failed to get History');
                }
            );
        }
    }

    isBookingDateLaterThanCurrent(item): boolean {
        let flag = false;
        const comparablePassDate = +moment.tz(item.dateTo.split('+')[0], (item.buildingTimezone ? item.buildingTimezone : 'America/Toronto')).format('x');
        const comparableCurrentDate = +moment().tz(item.buildingTimezone ? item.buildingTimezone : 'America/Toronto').format('x');
        if (comparablePassDate > comparableCurrentDate)
            flag = true;
        return flag;
    }

    isBookingStartDateLessThanCurrent(item): boolean {
        let flag = false;
        const comparablePassStartDate = +moment.tz(item.dateFrom.split('+')[0], (item.buildingTimezone ? item.buildingTimezone : 'America/Toronto')).format('x');
        const comparableCurrentDate = +moment().tz(item.buildingTimezone ? item.buildingTimezone : 'America/Toronto').format('x');
        if (comparablePassStartDate < comparableCurrentDate)
            flag = true;
        return flag;
    }

    printParkingPass(pass): void {
        let validPeriods = '';
        let firstPart = '<!DOCTYPE html>\n' +
            '<html>\n' +
            '    <head>\n' +
            '        <style>\n' +
            '            table{ \n' +
            '                max-width: 710px;\n' +
            '                max-height: 265px; \n' +
            '                border: 4px solid black;\n' +
            '                border-spacing: 0;\n' +
            '                border-collapse: collapse;\n' +
            '                font-family: Arial;\n' +
            '                margin-bottom: 10px;\n' +
            '            }\n' +
            '            th{ \n' +
            '                border-bottom: 1px solid black;\n' +
            '                font-size: 19px;\n' +
            '                padding-left: 10px\n' +
            '            }\n' +
            '            td{ \n' +
            '                border: 1px solid black;\n' +
            '                padding-left: 10px\n' +
            '            }\n' +
            '            .text-left{ text-align: left; }\n' +
            '            .first-th{ \n' +
            '                border-right: 1px solid black;\n' +
            '                width: 65%; \n' +
            '            }\n' +
            '            .font-size-26{ font-size: 26px; }\n' +
            '            .valid-periods-cell b{ font-size: 21px; }\n' +
            '            .font-size-11{ font-size: 11px; }\n' +
            '            .vertical-align-top{ vertical-align: top; }\n' +
            '            .main-wrapper{' +
            '                position: absolute;\n' +
            '                top: 0;' +
            '            }' +
            '        </style>\n' +
            '        <title>Parking Pass</title>\n' +
            '        <meta charset="UTF-8">\n' +
            '    </head>\n' +
            '    <body>\n' +
            '        <div class="main-wrapper">\n';
        let template = '';
        let secondPart = '        </div>\n' +
            '    </body>\n' +
            '</html>';
        let bookingPassTypes = JSON.parse(JSON.stringify(pass.bookingPassTypes));
        const passTypesNumber = pass.bookingPassTypes.length;
        for (let i = 0; i < passTypesNumber - 1; i++) {
            bookingPassTypes.forEach(type => {
                bookingPassTypes.forEach((secondRunType, index) => {
                    if (type.dateTo == secondRunType.dateFrom) {
                        type.dateTo = secondRunType.dateTo;
                        bookingPassTypes.splice(index, 1);
                    } else if (type.dateFrom == secondRunType.dateTo) {
                        type.dateFrom = secondRunType.dateFrom;
                        bookingPassTypes.splice(index, 1);
                    }
                });
            });
        }
        for (let i = 0; i < bookingPassTypes.length; i++) {
            validPeriods += '<br/> <b>' +
                moment.utc(bookingPassTypes[i].dateFrom).format('YYYY-MM-DD hh:mm A') +
                ' - ' +
                moment.utc(bookingPassTypes[i].dateTo).format('YYYY-MM-DD hh:mm A') +
                '</b>';
            if ((bookingPassTypes.length > 1 &&
                ((i + 1) % 3 == 0 || i == bookingPassTypes.length - 1)) ||
                bookingPassTypes.length == 1) {
                template +=
                    '            <table>\n' +
                    '                <tr>\n' +
                    `                    <th class="text-left first-th">` +
                    '                        <div style="display: inline-flex;">' +
                    `                            ${pass.buildingName}` +
                    `                            <div style="margin-left: 20px;">${this.printSuite(pass) ? pass.suiteNumber : ''}</div>` +
                    '                        </div>' +
                    '                    </th>\n' +
                    `                    <th class="text-left">Visitors parking permit #${pass.bookingNumber}</th>\n` +
                    '                </tr>\n' +
                    '                <tr>\n' +
                    '                    <td rowspan="2" class="font-size-26 vertical-align-top valid-periods-cell">\n' +
                    '                        Valid Periods\n' +
                    validPeriods +
                    '                    </td>\n' +
                    '                    <td class="font-size-26">\n' +
                    '                        License \n' +
                    '                        <br>\n' +
                    `                        <b>${pass.visitorLicensePlate}</b>\n` +
                    '                    </td>\n' +
                    '                </tr>\n' +
                    '                <tr>\n' +
                    '                    <td class="font-size-26">\n' +
                    '                        Vehicle \n' +
                    '                        <br>\n' +
                    `                        <b>${pass.visitorVehicle}</b>\n` +
                    '                    </td>\n' +
                    '                </tr>\n' +
                    '                <tr>\n' +
                    '                    <td colspan="2" class="font-size-11">\n' +
                    '                        Important: this permit must be visibly displayed on the dash of your vehicle to be considered valid. Permit is only valid until the indicated expiry date & time. The Corporation or Management will not be held responsible for any loss or damage to car or contents, however caused, including but not limited to fire, theft or collision. The user/holder of this parking permit is responsible for the accuracy of the information contained herein. Parking spots are available on a first come, first serve basis. This permit does not guarantee nor reserve a parking spot. Unregistered vehicles and registered vehicles with expired permits are subject to fine and\\or towing by parking enforcement.\n' +
                    '                    </td>\n' +
                    '                </tr>\n' +
                    '            </table>\n';
                validPeriods = '';
            }
        }
        var tab = window.open();
        tab.document.write(firstPart + template + secondPart);
        tab.stop();
        tab.print();
        tab.close();
    }

    viewParkingPass(parkingPass): void {
        this.parkingPassInParkingPassModal = parkingPass;
        this.isViewModalShow = true;
    }

    approveBooking(item): void {
        if (this.paymentIsPaid(item)) {
            let transitionStatus = '';
            if (item.status == 'pending')
                transitionStatus = 'approved';
            else if (item.status == 'declined')
                transitionStatus = 'declined_approved';
            const url = `/v1/booking/transit/${item.id}.json`;
            this.api.patch('domainParkingPass', url, {transition: transitionStatus}).subscribe(
                () => this.updateView('Status was successfully updated'),
                error => this.api.errorHandler(error, 'Status update failed!')
            );
        } else
            toastr.warning('All payments must be paid before you can confirm the booking.');
    }

    declineBooking(item): void {
        let transitionStatus = '';
        if (item.status == 'pending')
            transitionStatus = 'declined';
        else if (item.status == 'approved')
            transitionStatus = 'approved_declined';
        const url = `/v1/booking/transit/${item.id}.json`;
        this.api.patch('domainParkingPass', url, {transition: transitionStatus}).subscribe(
            () => this.updateView('Status was successfully updated'),
            error => this.api.errorHandler(error, 'Status update failed!')
        );
    }

    paymentIsPaid(item): boolean {
        return !item.payment || (item.payment && item.payment.status);
    }

    updateView(msg: string): void {
        toastr.success(msg);
        this.getData();
    }

    printSuite(pass): boolean {
        let canPrint = false;
        if (pass.bookingPassTypes) {
            pass.bookingPassTypes.filter(type => {
                if (type.passType.printSuiteOnParkingPass)
                    canPrint = true;
            });
        }
        return canPrint;
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/parking-pass.service.ts